import { useState, useEffect } from "react";
import { Loader2, X, Copy, RotateCw, Check, AlertCircle, ExternalLink, Mail, Smartphone, ShieldAlert, KeyRound, User } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("adminToken") || ""}` });

function formatTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
}

const STATUS_STYLE = {
  sent:    "bg-emerald-50 text-emerald-700 border-emerald-200",
  failed:  "bg-red-50 text-red-700 border-red-200",
  failed_permanent: "bg-red-50 text-red-700 border-red-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  resolved: "bg-blue-50 text-blue-700 border-blue-200",
};

export function CommunicationDetailModal({ logId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/communications/${logId}`, { headers: auth() });
        setData(r.data.communication);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to load detail");
        onClose?.();
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logId]);

  const handleResend = async () => {
    setActing(true);
    try {
      const r = await axios.post(`${API}/admin/communications/${logId}/resend`, {}, { headers: auth() });
      if (r.data.ok) toast.success("Email re-sent");
      else toast.error("Resend dispatched but provider reported a failure");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Resend failed");
    }
    setActing(false);
  };

  const handleResolve = async () => {
    if (!window.confirm("Mark this failed communication as resolved?")) return;
    setActing(true);
    try {
      await axios.post(`${API}/admin/communications/${logId}/resolve`, {}, { headers: auth() });
      toast.success("Marked resolved");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Resolve failed");
    }
    setActing(false);
  };

  const handleIssueResetLink = async () => {
    if (!data?.customer_id) {
      toast.error("No linked customer_id — open the customer profile and use 'Send Password Link' from there.");
      return;
    }
    if (!window.confirm("Issue a brand-new password reset link to this customer? The old one will not be reused.")) return;
    setActing(true);
    try {
      await axios.post(`${API}/admin/communications/issue-reset-link`, { customer_id: data.customer_id }, { headers: auth() });
      toast.success("Fresh reset link sent");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not issue link");
    }
    setActing(false);
  };

  const copyText = async (text, label) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="comms-detail-modal">
      <div className="bg-white max-w-3xl w-full p-6 border border-[#282828]/10 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {loading || !data ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#9A7B4F]" /></div>
        ) : (
          <>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                {data.to_phone ? <Smartphone className="w-5 h-5 text-[#9A7B4F]" /> : <Mail className="w-5 h-5 text-[#9A7B4F]" />}
                <div>
                  <h3 className="text-[18px] text-[#282828]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
                    {data.subject || data.message_preview || data.email_type}
                  </h3>
                  <div className="flex items-center gap-2 mt-0.5 text-[10px] uppercase tracking-wider text-[#676664]/60">
                    <span className="font-mono">{data.email_type}</span>
                    <span>·</span>
                    <span>{data.provider}</span>
                    <span>·</span>
                    <span className={`px-1.5 py-0.5 border ${STATUS_STYLE[data.status] || "bg-[#FAF8F0] text-[#676664] border-[#282828]/10"}`}>{data.status}</span>
                    {data.is_sensitive && (
                      <span className="inline-flex items-center gap-0.5 text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5">
                        <ShieldAlert className="w-2.5 h-2.5" /> redacted
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button onClick={onClose} className="p-1 text-[#676664]/40 hover:text-[#282828]" data-testid="close-comms-detail">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Actions row */}
            <div className="flex flex-wrap gap-2 mb-5 pb-5 border-b border-[#282828]/8">
              {!data.is_sensitive && data.booking_id && (
                <Button onClick={handleResend} disabled={acting} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[11px]" data-testid="action-resend">
                  {acting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5 mr-1.5" />}
                  Resend
                </Button>
              )}
              {data.is_sensitive && (data.email_type === "password_reset" || data.email_type === "password_setup_admin") && (
                <Button onClick={handleIssueResetLink} disabled={acting} className="bg-blue-700 hover:bg-blue-800 text-white text-[11px]" data-testid="action-issue-reset-link">
                  {acting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5 mr-1.5" />}
                  Issue new reset link
                </Button>
              )}
              {data.is_sensitive && data.email_type.startsWith("otp_") && (
                <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1.5 italic">
                  OTP codes cannot be resent — the customer should request a fresh code from checkout.
                </div>
              )}
              {(data.status === "failed" || data.status === "failed_permanent") && (
                <Button onClick={handleResolve} disabled={acting} variant="outline" className="border-blue-300 text-blue-700 text-[11px]" data-testid="action-resolve">
                  {acting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
                  Mark resolved
                </Button>
              )}
              <Button onClick={() => copyText(data.subject, "Subject")} variant="outline" className="border-[#282828]/10 text-[11px]" disabled={!data.subject} data-testid="action-copy-subject">
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Subject
              </Button>
              <Button onClick={() => copyText(data.body_html || data.message_preview, "Body")} variant="outline" className="border-[#282828]/10 text-[11px]" disabled={!data.body_html && !data.message_preview} data-testid="action-copy-body">
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Body
              </Button>
            </div>

            {/* Meta grid */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-[12px] mb-5">
              <Meta label="From" value={data.sender} mono />
              <Meta label="To" value={data.to_email || data.to_phone} mono />
              <Meta label="Recipient" value={data.recipient_name} />
              <Meta label="Direction" value={data.direction} />
              <Meta label="Created" value={formatTime(data.created_at)} />
              <Meta label="Sent" value={formatTime(data.sent_at)} />
              {data.failed_at && <Meta label="Failed at" value={formatTime(data.failed_at)} />}
              {data.last_retry_at && <Meta label="Last retry" value={formatTime(data.last_retry_at)} />}
              {data.resolved_at && <Meta label="Resolved at" value={formatTime(data.resolved_at)} />}
              <Meta label="Retry count" value={data.retry_count ?? 0} />
              <Meta label="Provider ID" value={data.provider_message_id || "—"} mono />
              <Meta label="Template" value={data.template_name} mono />
            </div>

            {/* Related links */}
            {(data.customer_id || data.booking_id || data.trial_lead_id || data.appointment_id) && (
              <div className="mb-5">
                <div className="text-[10px] uppercase tracking-wider text-[#676664]/60 mb-2">Related records</div>
                <div className="flex flex-wrap gap-1.5">
                  {data.customer_id && (
                    <Link to={`/admin/customers/${data.customer_id}`} className="text-[10px] text-[#9A7B4F] border border-[#9A7B4F]/30 px-2 py-1 hover:bg-[#9A7B4F]/5 inline-flex items-center gap-1" data-testid="related-customer">
                      <User className="w-3 h-3" /> Customer <ExternalLink className="w-2.5 h-2.5 opacity-50" />
                    </Link>
                  )}
                  {data.booking_id && !data.booking_id.startsWith("otp:") && !data.booking_id.startsWith("pwsetup:") && (
                    <Link to={`/admin/bookings`} className="text-[10px] text-[#9A7B4F] border border-[#9A7B4F]/30 px-2 py-1 hover:bg-[#9A7B4F]/5 inline-flex items-center gap-1" data-testid="related-booking">
                      Booking · {data.booking_id} <ExternalLink className="w-2.5 h-2.5 opacity-50" />
                    </Link>
                  )}
                  {data.trial_lead_id && (
                    <Link to={`/admin/trial-leads`} className="text-[10px] text-[#9A7B4F] border border-[#9A7B4F]/30 px-2 py-1 hover:bg-[#9A7B4F]/5 inline-flex items-center gap-1">
                      Trial · {data.trial_lead_id} <ExternalLink className="w-2.5 h-2.5 opacity-50" />
                    </Link>
                  )}
                </div>
              </div>
            )}

            {/* Error block */}
            {data.error_message && (
              <div className="mb-5 bg-red-50 border border-red-200 p-3" data-testid="comms-error-block">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="text-[12px] text-red-900 break-words">
                    <div className="font-semibold mb-0.5">Error</div>
                    <div className="text-[11px] text-red-700 whitespace-pre-wrap">{data.error_message}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Body */}
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#676664]/60 mb-2 flex items-center justify-between">
                <span>Body</span>
                {data.is_sensitive && <span className="text-[9px] text-amber-700 italic normal-case">Redacted at write-time to prevent code/token leaks</span>}
              </div>
              {data.body_html ? (
                <iframe
                  title="Email body preview"
                  srcDoc={data.body_html}
                  sandbox=""
                  className="w-full border border-[#282828]/10 bg-white"
                  style={{ height: 420 }}
                  data-testid="comms-body-iframe"
                />
              ) : data.message_preview ? (
                <div className="bg-[#FAF8F0] border border-[#282828]/8 p-3 text-[12px] text-[#282828] italic" data-testid="comms-body-preview">
                  {data.message_preview}
                </div>
              ) : (
                <div className="text-center py-8 bg-[#FAF8F0]/40 border border-[#282828]/8 text-[12px] text-[#676664]/60 italic" data-testid="comms-body-unavailable">
                  {data.body_unavailable_reason || "Content was not stored for this older email."}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Meta({ label, value, mono }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-[#676664]/50 mb-0.5">{label}</div>
      <div className={`text-[#282828] ${mono ? "font-mono text-[11px]" : "text-[12px]"}`}>{value || "—"}</div>
    </div>
  );
}
